import type { VehicleRegistration } from '@/features/vehiclesearch/types/vehicleRegistration.types';
import type { Snapshot } from '../types/agreements.types';
import { resolveRegistration } from './resolveRegistration';

export interface RegistrationView {
    /** What to show as the registration. Undefined only when neither source has one. */
    current: string | undefined;
    /** The sale-day plate, shown beneath `current` when `changed`. */
    asSold: string | undefined;
    /** True only when both are present and differ after canonicalisation. */
    changed: boolean;
}

const canonical = (vrm: string): string => vrm.replace(/\s+/g, '').toUpperCase();

/** A 7-character plate gets the UK space; anything else is returned unchanged. The same
 *  rule as VehicleStatus::getFormattedVrmAttribute(), applied to both sides of the
 *  comparison so the panel cannot show one plate spaced and the other not. */
export function formatVrm(vrm: string): string {
    const stripped = canonical(vrm);

    return stripped.length === 7 ? `${stripped.slice(0, 4)} ${stripped.slice(4)}` : stripped;
}

/** The live plate wins when there is one; the snapshot is the whole answer otherwise. */
export function resolveRegistrationView(
    snapshot: Snapshot | undefined,
    live: VehicleRegistration | undefined,
): RegistrationView {
    const snapshotVrm = snapshot ? resolveRegistration(snapshot) : undefined;
    const liveVrm = live?.vrm ?? undefined;

    if (!liveVrm) {
        return {
            current: snapshotVrm ? formatVrm(snapshotVrm) : undefined,
            asSold: undefined,
            changed: false,
        };
    }

    const changed = !!snapshotVrm && canonical(snapshotVrm) !== canonical(liveVrm);

    return {
        current: formatVrm(liveVrm),
        asSold: changed && snapshotVrm ? formatVrm(snapshotVrm) : undefined,
        changed,
    };
}
